import React, { useState, useRef, useEffect, useMemo } from 'react';
import { getUserDisplayName } from '../utils/roleUtils';

interface UserProfileOption {
  email: string;
  team: string;
  primaryTeam?: string;
}

interface EmailComboboxInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  userProfiles: UserProfileOption[];
  className?: string;
  inputClassName?: string;
}

export const EmailComboboxInput: React.FC<EmailComboboxInputProps> = ({
  value,
  onChange,
  placeholder = 'Type or select employee email...',
  userProfiles,
  className = '',
  inputClassName = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter profiles based on current typed input
  const filteredProfiles = useMemo(() => {
    const query = (value || '').toLowerCase().trim();
    if (!query) return userProfiles;

    return userProfiles.filter(p => {
      const name = getUserDisplayName({ email: p.email }).toLowerCase();
      return p.email.toLowerCase().includes(query) ||
             name.includes(query) ||
             (p.team && p.team.toLowerCase().includes(query));
    });
  }, [userProfiles, value]);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      <div className="relative flex items-center">
        <input
          type="email"
          value={value}
          onChange={e => {
            onChange(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className={`w-full pr-8 ${inputClassName || 'px-3 py-2 bg-gray-900 border border-gray-700 rounded-xl text-xs text-white font-mono focus:outline-none focus:ring-1 focus:ring-amber-500'}`}
        />
        <button
          type="button"
          onClick={() => setIsOpen(prev => !prev)}
          className="absolute right-2 p-1 text-gray-400 hover:text-white transition-colors"
          title="Toggle user dropdown list"
        >
          <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Floating Dropdown List */}
      {isOpen && (
        <div className="absolute left-0 right-0 top-full mt-1 max-h-56 overflow-y-auto bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-50 divide-y divide-gray-800">
          {filteredProfiles.length > 0 ? (
            filteredProfiles.map(u => {
              const isSelected = value.toLowerCase().trim() === u.email.toLowerCase().trim();
              return (
                <button
                  key={u.email}
                  type="button"
                  onClick={() => {
                    onChange(u.email);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-xs transition-colors flex items-center justify-between hover:bg-amber-600/20 hover:text-amber-200 ${
                    isSelected ? 'bg-amber-600/30 text-amber-300 font-bold' : 'text-gray-200'
                  }`}
                >
                  <div className="flex flex-col min-w-0 pr-2">
                    <span className="font-semibold text-gray-200 truncate">{getUserDisplayName({ email: u.email })}</span>
                    <span className="font-mono text-[10px] text-gray-400 truncate">{u.email}</span>
                  </div>
                  {(() => {
                    const primary = u.primaryTeam || (u.team ? u.team.split(',')[0].trim() : 'No Team');
                    return (
                      <span className={`text-[10px] px-2 py-0.5 rounded font-semibold flex-shrink-0 ${
                        primary === 'No Team' ? 'bg-gray-800 text-gray-400 border border-gray-700' : 'bg-amber-900/60 text-amber-200 border border-amber-700/50'
                      }`}>
                        {primary}
                      </span>
                    );
                  })()}
                </button>
              );
            })
          ) : (
            <div className="px-3 py-2.5 text-xs text-gray-400 text-center italic">
              No matching active user found
            </div>
          )}
        </div>
      )}
    </div>
  );
};
